import { RECORD_FILE_LOST } from './apiErrorCode';
import { strongStorageOn } from './offlineRecords';
import { isRecordFileRef, readRecordFile, saveRecordFile, type RecordFileRef } from './recordStore';

/**
 * Запись голоса в очереди отправки: что в ней лежит и как это достать.
 *
 * ⚠️ ПОЛЯ ТОЛЬКО ДОБАВЛЯЮТСЯ. В очереди у учеников лежат записи, поставленные прошлой версией приложения:
 * у них есть `audio` и нет ни `file`, ни `clientId`. Обработчик обязан отправлять и такие.
 */
export type LessonAudioPayload = {
  blockId: string;
  /** Байты записи — прежний путь, IndexedDB. */
  audio?: ArrayBuffer;
  /** Файл записи на телефоне — слой В (`lib/recordStore.ts`). Ровно одно из двух: `audio` или `file`. */
  file?: RecordFileRef;
  mime: string;
  blockType?: string;
  clientSeconds?: number;
  stopReason?: string;
  /** UUID записи, рождается в момент «Стоп» (договор `audio-upload.md` §1). Шлётся только при включённом слое Б. */
  clientId?: string;
  /** Когда запись остановлена, по часам устройства: от него считается `age_ms`. */
  stoppedAt?: number;
  /**
   * Урок и раздел — для списка «Неотправленные записи» в профиле и «Сдать» в «Домашках» (промпт 22, 18.10.2026).
   * На сервер не уходят. У записей прежних версий их нет: в профиле такая — «Задание из урока», в «Домашках» её нет.
   */
  lessonId?: string;
  lessonTitle?: string;
  sectionId?: string;
  sectionTitle?: string;
};

export const LESSON_AUDIO_KIND = 'lesson-audio';

export const lessonAudioId = (blockId: string) => `${LESSON_AUDIO_KIND}:${blockId}`;


/**
 * UUID v4 для записи. Случайность обязательна: `client_id` попадает в адрес файла, а раздача файлов
 * публичная (договор §1).
 */
export function newClientId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const b = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') c.getRandomValues(b);
  else for (let i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map((x) => x.toString(16).padStart(2, '0'));
  return `${h.slice(0, 4).join('')}-${h.slice(4, 6).join('')}-${h.slice(6, 8).join('')}-${h.slice(8, 10).join('')}-${h.slice(10).join('')}`;
}

/** Расширение файла из типа записи: айфон пишет mp4, а не webm. У повтора оно обязано быть тем же (договор §2). */
export function recordExt(mime: string): string {
  return ((mime || 'audio/webm').split('/')[1] || 'webm').split(';')[0].trim() || 'webm';
}

/** Сколько запись пролежала, мс: 0…30 суток, как принимает сервер (договор §3). Неизвестно — `undefined`. */
export function recordAgeMs(payload: Pick<LessonAudioPayload, 'stoppedAt'>, now = Date.now()): number | undefined {
  if (typeof payload.stoppedAt !== 'number' || !Number.isFinite(payload.stoppedAt)) return undefined;
  const age = Math.round(now - payload.stoppedAt);
  return Math.min(Math.max(age, 0), 30 * 24 * 60 * 60 * 1000);
}

/**
 * Собрать запись для очереди.
 *
 * Слой В включён — байты ложатся файлом на телефон, в очередь идёт ссылка. Файл не записался (нет места,
 * мост отказал) — байты идут в очередь прежним путём: запись ученика важнее способа хранения.
 */
export async function buildLessonAudioPayload(input: {
  blockId: string;
  blob: Blob;
  blockType?: string;
  clientSeconds?: number;
  stopReason?: string;
  clientId?: string;
  stoppedAt?: number;
  lessonId?: string;
  lessonTitle?: string;
  sectionId?: string;
  sectionTitle?: string;
}): Promise<LessonAudioPayload> {
  const { blob, ...rest } = input;
  const mime = blob.type || 'audio/webm';
  // В очередь кладём сами БАЙТЫ записи, а не объект файла. Байты умеет хранить любое хранилище на любом
  // устройстве; на объект файла полагаться нельзя — на части телефонов он сохраняется пустым, и запись
  // молча превратилась бы в ноль байт.
  const audio = await blob.arrayBuffer();
  if (strongStorageOn()) {
    // Имя — по записи, не по заданию: перезаписанный ответ не должен попасть в файл прежнего, который
    // очередь в этот момент может читать.
    const name = `${rest.clientId ?? newClientId()}.${recordExt(mime)}`;
    const file = await saveRecordFile(name, audio);
    if (file) return { ...rest, mime, file };
  }
  return { ...rest, mime, audio };
}

/** Байты записи — из очереди или из файла. Файла нет — отказ «навсегда» с кодом `record_file_lost`. */
export async function loadLessonAudio(payload: LessonAudioPayload): Promise<ArrayBuffer> {
  if (payload.audio && payload.audio.byteLength) return payload.audio;
  if (isRecordFileRef(payload.file)) {
    try {
      return await readRecordFile(payload.file);
    } catch {
      /* ниже */
    }
  }
  // Вид отказа — как у ответа сервера (`lib/apiErrorCode.ts`): очередь снимет запись и скажет ученику,
  // а не будет повторять отправку пустоты каждые пять минут.
  throw Object.assign(new Error('record file lost'), { status: 410, raw: { code: RECORD_FILE_LOST } });
}
